// src/encounter/intelligence/intel-warm.ts
//
// Warm-from-Today (Samer, 4 Nov 2026: "i don't want lack of reliability, i will not remember those instructions"). When
// the clinician's Today screen loads, ONE warm Explain (Heart & vessels) runs for the clinician's NEXT visit, with the
// clinician's own token, so the slow first look of the AI check is done before the visit opens. The engine (9ed2438)
// lets the visit's own Explain JOIN a warm still running instead of meeting the per-clinician lock; the lock and the
// rate limit are unchanged, and a warm is skipped (202) while a visit Explain runs. It writes nothing to the chart and
// shows nothing: the answer is discarded here. Skipped when the clinician is not listed or has not confirmed the notice.
// The request carries exactly what the visit's Explain will send (patient, encounter, appointment, system), so a join
// can match. Behind the encounter switch `intelWarm` (`?x=intel-warm`), ON BY DEFAULT on the dev hosts.
import { ACK_PATH, parseAckNotice } from './acknowledgement';
import { EXPLAIN_PATH, newRequestId } from './use-explain';

type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

export interface WarmTarget { appointmentId: string; patientId: string }
export interface WarmDeps {
  fetch: FetchLike;
  token: () => string | null;
  /** The visit's encounter id as the visit screen resolves it (GET by appointment), or null before an encounter exists. */
  encounterOf: (appointmentId: string) => Promise<string | null>;
  newId?: () => string;
}

/** Appointments already warmed in this browser session: one warm per visit. */
const warmed = new Set<string>();
export const resetWarmedForTests = () => warmed.clear();

export type WarmOutcome = 'sent' | 'skipped-repeat' | 'skipped-no-token' | 'skipped-notice' | 'failed';

export async function warmNextVisit(t: WarmTarget, d: WarmDeps): Promise<WarmOutcome> {
  if (warmed.has(t.appointmentId)) return 'skipped-repeat';
  const access = d.token();
  if (!access) return 'skipped-no-token';
  warmed.add(t.appointmentId);
  const headers = { authorization: `Bearer ${access}`, accept: 'application/json' };
  try {
    const ack = await d.fetch(ACK_PATH, { method: 'GET', headers });
    const notice = ack.ok ? parseAckNotice(await ack.json().catch(() => null)) : null;
    if (!notice || !notice.listed || !notice.acknowledged) {
      warmed.delete(t.appointmentId);
      return 'skipped-notice';
    }
    // null only for a confirmed "no encounter yet" (404); a failed or stalled lookup skips the warm (Codex): a warm with
    // the wrong binding would warm another context.
    const encounterId = await d.encounterOf(t.appointmentId);
    const body = {
      patientId: t.patientId,
      encounterId,
      requestId: (d.newId ?? newRequestId)(),
      system: 'heart-vessels',
      ...(encounterId ? { appointmentId: t.appointmentId } : {}),
      warm: true,
    };
    const res = await d.fetch(EXPLAIN_PATH, { method: 'POST', headers: { ...headers, 'content-type': 'application/json' }, body: JSON.stringify(body) });
    // The answer is not shown or kept here: the engine keeps the passed check for the visit.
    await res.text().catch(() => '');
    return 'sent';
  } catch {
    // A failed warm changes nothing for the visit, which runs its own Explain as before.
    warmed.delete(t.appointmentId);
    return 'failed';
  }
}
